/**
 * Remediation Store
 * 
 * Zustand store for managing remediation actions:
 * - Job queue management
 * - Real-time progress updates
 * - Maintenance windows
 * - Stats tracking
 * 
 * Peer reviewed: Gemini 2.5 Pro, GPT-4o
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  RemediationJob,
  RemediationStats,
  MaintenanceWindow,
  DeviceRemediationResult,
  RemediationProgressUpdate,
} from '../types/remediation';

// ============================================================================
// State Interface
// ============================================================================

interface RemediationState {
  // Jobs
  jobs: Map<string, RemediationJob>;
  selectedJobId: string | null;
  
  // Maintenance Windows
  maintenanceWindows: Map<string, MaintenanceWindow>;
  
  // Stats
  stats: RemediationStats | null;
  
  // UI State
  isLoading: boolean;
  isExecuting: boolean;
  error: string | null;
  
  // Undo tracking
  undoableJobId: string | null;
  undoTimeoutId: ReturnType<typeof setTimeout> | null;
}

// ============================================================================
// Actions Interface
// ============================================================================

interface RemediationActions {
  // Job CRUD
  setJobs: (jobs: RemediationJob[]) => void;
  addJob: (job: RemediationJob) => void;
  updateJob: (id: string, updates: Partial<RemediationJob>) => void;
  removeJob: (id: string) => void;
  
  // Job operations
  cancelJob: (id: string) => void;
  rescheduleJob: (id: string, newScheduledAt: string) => void;
  reorderJobs: (fromIndex: number, toIndex: number) => void;
  
  // Selection
  selectJob: (id: string | null) => void;
  
  // Progress updates (from WebSocket)
  handleProgressUpdate: (update: RemediationProgressUpdate) => void;
  updateDeviceResult: (jobId: string, deviceResult: DeviceRemediationResult) => void;
  
  // Undo
  setUndoableJob: (jobId: string | null, timeoutMs?: number | undefined) => void;
  executeUndo: (jobId: string) => void;
  
  // Maintenance Windows
  setMaintenanceWindows: (windows: MaintenanceWindow[]) => void;
  addMaintenanceWindow: (window: MaintenanceWindow) => void;
  updateMaintenanceWindow: (id: string, updates: Partial<MaintenanceWindow>) => void;
  deleteMaintenanceWindow: (id: string) => void;
  isInMaintenanceWindow: (time?: Date | undefined) => MaintenanceWindow | null;
  
  // Stats
  setStats: (stats: RemediationStats) => void;
  
  // Loading states
  setIsLoading: (isLoading: boolean) => void;
  setIsExecuting: (isExecuting: boolean) => void;
  setError: (error: string | null) => void;
  
  // Selectors
  getPendingJobs: () => RemediationJob[];
  getScheduledJobs: () => RemediationJob[];
  getActiveJobs: () => RemediationJob[];
  getCompletedJobs: () => RemediationJob[];
  
  // Reset
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialStats: RemediationStats = {
  totalToday: 0,
  totalThisWeek: 0,
  pendingCount: 0,
  scheduledCount: 0,
  inProgressCount: 0,
  completedCount: 0,
  failedCount: 0,
  successRate: 0,
  avgCompletionTimeMs: 0,
  devicesRemediated: 0,
};

const initialState: RemediationState = {
  jobs: new Map(),
  selectedJobId: null,
  maintenanceWindows: new Map(),
  stats: initialStats,
  isLoading: false,
  isExecuting: false,
  error: null,
  undoableJobId: null,
  undoTimeoutId: null,
};

// ============================================================================
// Store Creation
// ============================================================================

export const useRemediationStore = create<RemediationState & RemediationActions>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,

        // ----------------------------------------------------------------------
        // Job CRUD
        // ----------------------------------------------------------------------
        
        setJobs: (jobs: RemediationJob[]) => {
          set((state) => {
            state.jobs.clear();
            jobs.forEach((j) => state.jobs.set(j.id, j));
          });
        },
        
        addJob: (job: RemediationJob) => {
          set((state) => {
            state.jobs.set(job.id, job);
          });
        },
        
        updateJob: (id: string, updates: Partial<RemediationJob>) => {
          set((state) => {
            const existing = state.jobs.get(id);
            if (existing) {
              state.jobs.set(id, { ...existing, ...updates });
            }
          });
        },
        
        removeJob: (id: string) => {
          set((state) => {
            state.jobs.delete(id);
            if (state.selectedJobId === id) {
              state.selectedJobId = null;
            }
          });
        },

        // ----------------------------------------------------------------------
        // Job Operations
        // ----------------------------------------------------------------------
        
        cancelJob: (id: string) => {
          set((state) => {
            const job = state.jobs.get(id);
            if (job && (job.status === 'pending' || job.status === 'scheduled')) {
              job.status = 'cancelled';
              job.auditTrail.push({
                id: crypto.randomUUID(),
                timestamp: new Date().toISOString(),
                action: 'cancelled',
                performedBy: 'current-user',
              });
            }
          });
        },
        
        rescheduleJob: (id: string, newScheduledAt: string) => {
          set((state) => {
            const job = state.jobs.get(id);
            if (job && job.status === 'scheduled') {
              job.scheduledAt = newScheduledAt;
              job.auditTrail.push({
                id: crypto.randomUUID(),
                timestamp: new Date().toISOString(),
                action: 'rescheduled',
                performedBy: 'current-user',
                details: `Rescheduled to ${newScheduledAt}`,
              });
            }
          });
        },
        
        reorderJobs: (fromIndex: number, toIndex: number) => {
          set((state) => {
            const jobsArray = Array.from(state.jobs.values())
              .filter((j) => j.status === 'pending' || j.status === 'scheduled')
              .sort((a, b) => a.priority - b.priority);
            
            const [moved] = jobsArray.splice(fromIndex, 1);
            if (moved) {
              jobsArray.splice(toIndex, 0, moved);
              
              // Update priorities
              jobsArray.forEach((job, i) => {
                const existing = state.jobs.get(job.id);
                if (existing) {
                  existing.priority = i + 1;
                }
              });
            }
          });
        },

        // ----------------------------------------------------------------------
        // Selection
        // ----------------------------------------------------------------------
        
        selectJob: (id: string | null) => {
          set((state) => {
            state.selectedJobId = id;
          });
        },

        // ----------------------------------------------------------------------
        // Progress Updates
        // ----------------------------------------------------------------------
        
        handleProgressUpdate: (update: RemediationProgressUpdate) => {
          set((state) => {
            const job = state.jobs.get(update.jobId);
            if (job) {
              job.status = update.status;
              if (update.progress) {
                job.progress = update.progress;
              }
              if (update.deviceResults) {
                job.deviceResults = update.deviceResults;
              }
              if (update.error) {
                job.error = update.error;
              }
              if (update.status === 'completed' || update.status === 'failed' || update.status === 'completed-with-errors') {
                job.completedAt = new Date().toISOString();
              }
            }
          });
        },
        
        updateDeviceResult: (jobId: string, deviceResult: DeviceRemediationResult) => {
          set((state) => {
            const job = state.jobs.get(jobId);
            if (job) {
              const index = job.deviceResults.findIndex((d) => d.deviceId === deviceResult.deviceId);
              if (index !== -1) {
                job.deviceResults[index] = deviceResult;
              } else {
                job.deviceResults.push(deviceResult);
              }
              
              // Update progress
              const completed = job.deviceResults.filter((d) => d.status === 'success' || d.status === 'failed').length;
              job.progress = {
                completedCount: completed,
                totalCount: job.targetDeviceIds.length,
                currentDeviceName: deviceResult.deviceName,
              };
            }
          });
        },

        // ----------------------------------------------------------------------
        // Undo
        // ----------------------------------------------------------------------
        
        setUndoableJob: (jobId: string | null, timeoutMs = 5000) => {
          const existing = get().undoTimeoutId;
          if (existing) {
            clearTimeout(existing);
          }
          
          if (jobId) {
            const timeoutId = setTimeout(() => {
              set((state) => {
                state.undoableJobId = null;
                state.undoTimeoutId = null;
                
                // Also update the job to mark it as not undoable
                const job = state.jobs.get(jobId);
                if (job) {
                  job.canUndo = false;
                }
              });
            }, timeoutMs);
            
            set((state) => {
              state.undoableJobId = jobId;
              state.undoTimeoutId = timeoutId;
              
              const job = state.jobs.get(jobId);
              if (job) {
                job.canUndo = true;
                job.undoDeadline = new Date(Date.now() + timeoutMs).toISOString();
              }
            });
          } else {
            set((state) => {
              state.undoableJobId = null;
              state.undoTimeoutId = null;
            });
          }
        },
        
        executeUndo: (jobId: string) => {
          const existing = get().undoTimeoutId;
          if (existing) {
            clearTimeout(existing);
          }
          
          set((state) => {
            const job = state.jobs.get(jobId);
            if (job?.canUndo) {
              job.status = 'cancelled';
              job.canUndo = false;
              job.auditTrail.push({
                id: crypto.randomUUID(),
                timestamp: new Date().toISOString(),
                action: 'undone',
                performedBy: 'current-user',
              });
            }
            state.undoableJobId = null;
            state.undoTimeoutId = null;
          });
        },

        // ----------------------------------------------------------------------
        // Maintenance Windows
        // ----------------------------------------------------------------------
        
        setMaintenanceWindows: (windows: MaintenanceWindow[]) => {
          set((state) => {
            state.maintenanceWindows.clear();
            windows.forEach((w) => state.maintenanceWindows.set(w.id, w));
          });
        },
        
        addMaintenanceWindow: (window: MaintenanceWindow) => {
          set((state) => {
            state.maintenanceWindows.set(window.id, window);
          });
        },
        
        updateMaintenanceWindow: (id: string, updates: Partial<MaintenanceWindow>) => {
          set((state) => {
            const existing = state.maintenanceWindows.get(id);
            if (existing) {
              state.maintenanceWindows.set(id, { ...existing, ...updates });
            }
          });
        },
        
        deleteMaintenanceWindow: (id: string) => {
          set((state) => {
            state.maintenanceWindows.delete(id);
          });
        },
        
        isInMaintenanceWindow: (time?: Date | undefined) => {
          const checkTime = time || new Date();
          const windows = Array.from(get().maintenanceWindows.values()).filter((w) => w.isActive);
          
          for (const window of windows) {
            const dayOfWeek = checkTime.getDay();
            if (!window.daysOfWeek.includes(dayOfWeek)) {continue;}
            
            const [startHour, startMin] = window.startTime.split(':').map(Number);
            const [endHour, endMin] = window.endTime.split(':').map(Number);
            
            const startMinutes = (startHour ?? 0) * 60 + (startMin ?? 0);
            const endMinutes = (endHour ?? 0) * 60 + (endMin ?? 0);
            const currentMinutes = checkTime.getHours() * 60 + checkTime.getMinutes();
            
            if (currentMinutes >= startMinutes && currentMinutes <= endMinutes) {
              return window;
            }
          }
          
          return null;
        },

        // ----------------------------------------------------------------------
        // Stats
        // ----------------------------------------------------------------------
        
        setStats: (stats: RemediationStats) => {
          set((state) => {
            state.stats = stats;
          });
        },

        // ----------------------------------------------------------------------
        // Loading States
        // ----------------------------------------------------------------------
        
        setIsLoading: (isLoading: boolean) => {
          set((state) => {
            state.isLoading = isLoading;
          });
        },
        
        setIsExecuting: (isExecuting: boolean) => {
          set((state) => {
            state.isExecuting = isExecuting;
          });
        },
        
        setError: (error: string | null) => {
          set((state) => {
            state.error = error;
            state.isLoading = false;
            state.isExecuting = false;
          });
        },

        // ----------------------------------------------------------------------
        // Selectors
        // ----------------------------------------------------------------------
        
        getPendingJobs: () => {
          return Array.from(get().jobs.values())
            .filter((j) => j.status === 'pending')
            .sort((a, b) => a.priority - b.priority);
        },
        
        getScheduledJobs: () => {
          return Array.from(get().jobs.values())
            .filter((j) => j.status === 'scheduled')
            .sort((a, b) => new Date(a.scheduledAt || 0).getTime() - new Date(b.scheduledAt || 0).getTime());
        },
        
        getActiveJobs: () => {
          return Array.from(get().jobs.values())
            .filter((j) => j.status === 'in-progress');
        },
        
        getCompletedJobs: () => {
          return Array.from(get().jobs.values())
            .filter((j) => j.status === 'completed' || j.status === 'completed-with-errors' || j.status === 'failed')
            .sort((a, b) => new Date(b.completedAt || 0).getTime() - new Date(a.completedAt || 0).getTime());
        },

        // ----------------------------------------------------------------------
        // Reset
        // ----------------------------------------------------------------------
        
        reset: () => {
          const existing = get().undoTimeoutId;
          if (existing) {
            clearTimeout(existing);
          }
          set(() => ({ ...initialState }));
        },
      }))
    ),
    { name: 'remediation-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectJobs = (state: RemediationState) => state.jobs;
export const selectSelectedJob = (state: RemediationState & RemediationActions) =>
  state.selectedJobId ? state.jobs.get(state.selectedJobId) : undefined;
export const selectStats = (state: RemediationState) => state.stats;
export const selectMaintenanceWindows = (state: RemediationState) => state.maintenanceWindows;
export const selectIsLoading = (state: RemediationState) => state.isLoading;
export const selectIsExecuting = (state: RemediationState) => state.isExecuting;
export const selectError = (state: RemediationState) => state.error;
export const selectUndoableJobId = (state: RemediationState) => state.undoableJobId;

